import type { ComponentChildren } from "preact";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { t } from "../i18n/index.ts";
import { closePeek, moving, otherSide, pin, placement, type Side } from "../lib/peek.ts";
import { dragged, SNAPS, type Snap, settle } from "../lib/sheet.ts";
import { IconButton } from "./Button.tsx";
import { CloseIcon, PinIcon, PinnedIcon, SwapSidesIcon } from "./icons.tsx";
import { DOCKED_WIDTH, FOCUS } from "./styles.ts";

/** Gone once it has slid out (its animation's length, a little over: where animations don't run, it goes then). */
const LEAVE_MS = 300;

/** Escape closes the look unless something over it (a dialog, a menu) takes it. */
function useEscape(on: boolean): void {
  // As soon as it's drawn (an Escape right away closes it too).
  useLayoutEffect(() => {
    if (!on) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented && !document.querySelector("dialog[open], :popover-open")) {
        closePeek();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [on]);
}

/** As long as the page takes to make room on the other side (`Shell`'s padding transition). */
const ACROSS_MS = 300;

/**
 * Pinned, it goes to the other side across the page (from where it was, a width away, to where it is now; the page
 * crossing it the other way).
 */
function useAcross(box: { current: HTMLElement | null }, side: Side, pinned: boolean): void {
  const was = useRef(side);
  useLayoutEffect(() => {
    const from = was.current;
    was.current = side;
    if (from === side || !pinned || globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    box.current?.animate?.([{ translate: `${side === "right" ? -100 : 100}% 0` }, { translate: "0 0" }], {
      duration: ACROSS_MS,
      easing: "ease-out",
    });
  }, [box, side, pinned]);
}

/**
 * On a phone, a sheet at the bottom: rounded atop, as tall as `--sheet`, clear of the home indicator, rising from below
 * (the slide's `--rise`).
 */
const SHEET =
  "max-md:top-auto max-md:h-[var(--sheet)] max-md:rounded-t-2xl max-md:border-t max-md:pb-[env(safe-area-inset-bottom)] max-md:shadow-2xl max-md:[--edge:0] max-md:[--rise:1]";
/** Going to a height when let go (not while dragged: it follows the finger). */
const SETTLING = "max-md:motion-safe:transition-[height] max-md:motion-safe:duration-300 max-md:motion-safe:ease-out";

const PERCENT = 100;
/** A drag of the sheet's handle moving less than this is a tap. */
const DRAG = 4;

interface HandlePress {
  /** Where it went down, and the sheet's height then. */
  y: number;
  height: number;
  /** The last two places it moved to, and when (how fast it's going when let go). */
  last: { y: number; t: number };
  prev: { y: number; t: number };
  moved: boolean;
}

/** The handle's press, if it's down; and whether it was a drag (the click after it then isn't a tap). */
function idle(): { press: HandlePress | null; wasDrag: boolean } {
  return { press: null, wasDrag: false };
}

/**
 * On a phone, the look is a sheet from the bottom: half the screen or nearly all of it (`snap`), or where its handle's
 * being dragged to (`drag`, px). Let go, it settles (see `settle`): on a height, or closed. A tap on the handle (or
 * Enter on it) goes from one height to the other.
 */
function useSheet() {
  const [snap, setSnap] = useState<Snap>("half");
  const [drag, setDrag] = useState<number | null>(null);
  const s = useRef(idle()).current;
  const to = (p: HandlePress, y: number) => dragged(p.height - (y - p.y), globalThis.innerHeight);
  const handle = {
    onPointerDown: (e: PointerEvent) => {
      const el = e.currentTarget as HTMLElement;
      const at = { y: e.clientY, t: e.timeStamp };
      const { height } = (el.closest("[data-peek]") as HTMLElement).getBoundingClientRect();
      s.press = { y: e.clientY, height, last: at, prev: at, moved: false };
      s.wasDrag = false;
      el.setPointerCapture(e.pointerId);
    },
    onPointerMove: (e: PointerEvent) => {
      const p = s.press;
      if (!p) {
        return;
      }
      p.prev = p.last;
      p.last = { y: e.clientY, t: e.timeStamp };
      p.moved ||= Math.abs(e.clientY - p.y) >= DRAG;
      if (p.moved) {
        setDrag(to(p, e.clientY));
      }
    },
    onPointerUp: (e: PointerEvent) => {
      const p = s.press;
      s.press = null;
      if (!p?.moved) {
        return;
      }
      s.wasDrag = true;
      const dt = p.last.t - p.prev.t;
      const where = settle(to(p, e.clientY), globalThis.innerHeight, dt > 0 ? (p.last.y - p.prev.y) / dt : 0);
      if (where === "closed") {
        // It slides out from where it was let go.
        closePeek();
      } else {
        setSnap(where);
        setDrag(null);
      }
    },
    onPointerCancel: () => {
      s.press = null;
      setDrag(null);
    },
    onClick: () => {
      if (s.wasDrag) {
        s.wasDrag = false;
      } else {
        setSnap(snap === "half" ? "full" : "half");
      }
    },
  };
  return { snap, height: drag === null ? `${SNAPS[snap] * PERCENT}dvh` : `${drag}px`, dragging: drag !== null, handle };
}

/**
 * A quick look's frame, where its placement puts it: docked, over the page on one side, full height, sliding in from
 * its side; on a phone, a sheet from the bottom (see `useSheet`), the page still in view above it. Not modal: the rest
 * of the page stays usable. Its bar: `title`, then `actions`, then close. `leaving`: closed, sliding back out, then
 * `onGone`.
 */
export function PeekFrame({
  label,
  title,
  actions,
  leaving,
  onGone,
  children,
}: {
  label: string;
  title: ComponentChildren;
  actions?: ComponentChildren;
  leaving: boolean;
  onGone: () => void;
  children: ComponentChildren;
}) {
  useEscape(!leaving);
  useEffect(() => {
    if (!leaving) {
      return;
    }
    const gone = setTimeout(onGone, LEAVE_MS);
    return () => clearTimeout(gone);
  }, [leaving, onGone]);
  const box = useRef<HTMLElement>(null);
  // Focus comes in when the look opens (keyboards go on from there; Escape goes back).
  useEffect(() => box.current?.focus(), []);
  const p = placement.value;
  useAcross(box, p.side, p.pinned);
  // Out: closed, or on its way to the other side.
  const out = leaving || moving.value;
  const sheet = useSheet();
  return (
    <section
      ref={box}
      data-peek=""
      role="dialog"
      aria-modal="false"
      aria-label={label}
      tabIndex={-1}
      inert={out}
      style={{ "--sheet": sheet.height }}
      class={`fixed inset-0 z-30 flex flex-col border-zinc-200 bg-white outline-none ${SHEET} ${sheet.dragging ? "" : SETTLING} ${DOCKED_WIDTH} ${p.pinned ? "" : "md:shadow-2xl"} ${out ? "motion-safe:animate-slide-out" : "motion-safe:animate-slide-in"} ${p.side === "left" ? "md:right-auto md:border-r md:[--edge:-1]" : "md:left-auto md:border-l"}`}
    >
      {/* On a phone: the sheet's handle (a tap, or Enter, from one height to the other; dragged, it resizes). */}
      <button
        type="button"
        aria-label={t(sheet.snap === "half" ? "peek.expand" : "peek.shrink")}
        title={t(sheet.snap === "half" ? "peek.expand" : "peek.shrink")}
        class={`flex h-6 w-full shrink-0 cursor-grab touch-none items-center justify-center rounded-t-2xl md:hidden ${FOCUS}`}
        {...sheet.handle}
      >
        <span aria-hidden="true" class="h-1.5 w-10 rounded-full bg-zinc-300" />
      </button>
      <header class="flex items-start gap-2 border-b border-zinc-200 px-4 py-3">
        <div class="min-w-0 flex-1">{title}</div>
        <div class="flex shrink-0 items-center gap-1">
          {actions}
          {/* Where it sits: beside the page or over it, and which side (on a phone it's the whole screen). */}
          <IconButton
            label={t(p.pinned ? "peek.unpin" : "peek.pin")}
            aria-pressed={p.pinned}
            class="max-md:hidden"
            onClick={() => pin(!p.pinned)}
          >
            {p.pinned ? <PinnedIcon size={18} /> : <PinIcon size={18} />}
          </IconButton>
          <IconButton label={t("peek.otherSide")} class="max-md:hidden" onClick={otherSide}>
            <SwapSidesIcon size={18} />
          </IconButton>
          <IconButton label={t("common.dismiss")} onClick={closePeek}>
            <CloseIcon size={18} />
          </IconButton>
        </div>
      </header>
      <div class="flex min-h-0 flex-1 flex-col overflow-auto overscroll-contain">{children}</div>
    </section>
  );
}
